export default `# AR Glasses — Facilitator HUD

The AR Glasses feature turns a pair of **Even Realities G2 glasses** and an **R1 smart ring** into a heads-up display for the session facilitator. See pending contributions, session health, and alerts at a glance — and accept or ignore suggestions with a tap of the ring, all without looking at a screen.

---

## What You See

The G2 glasses have a **640×350 monochrome Micro LED** display that overlays information in your field of view. There are two display modes:

### HUD Mode (default)

A persistent overlay that stays visible while you facilitate:

| Zone | Content |
|------|---------|
| **Status bar** | Current phase, phase progress (e.g. 2/5), elapsed time, health score |
| **Contributions list** | Up to 5 pending avatar suggestions, scrollable |
| **Hint bar** | Ring gesture reminders: "scroll:nav · tap:accept · 2×:ignore" |
| **Notification area** | Temporary alerts (auto-dismiss after 4 seconds) |

Each contribution shows the avatar name and a summary, with an urgency marker:
- **[!]** — Urgent
- **[\\*]** — Important
- **[ ]** — Normal

### Switchable Mode

Full-screen views you cycle through for deeper detail:

| Screen | Content |
|--------|---------|
| **Status** | Phase info, health bar, participant count, auto-respond status, recent task completions |
| **Contributions** | Full pending contributions list with urgency and detail |
| **Alerts** | Active alerts sorted by priority (high → medium → low) |

Toggle between HUD and Switchable mode with a **long press** on the ring.

---

## Ring Controls

The R1 smart ring sits on your finger and gives you gesture-based control without touching any device.

### Gesture Reference

| Gesture | In HUD Mode | In Switchable Mode |
|---------|------------|-------------------|
| **Scroll up/down** | Navigate the contribution list | Contributions screen: scroll list. Other screens: switch between screens |
| **Single tap** | Accept selected contribution. If nothing selected: request new contributions from all avatars | Same as HUD. On Phases screen: advance to next phase |
| **Double tap** | Ignore selected contribution | Ignore selected contribution |
| **Long press** | Switch to Switchable mode | Switch to HUD mode |

### Gesture Tips

- **Scroll first, then tap** — Scroll to highlight the contribution you want, then tap to accept or double-tap to ignore
- **Tap with nothing selected** — A quick way to ask all avatars for fresh contributions
- **Long press to toggle** — Switch between the compact HUD overlay and full-screen detail views as needed

---

## Alerts

The glasses display real-time alerts when session health needs attention:

| Alert | Trigger | Priority |
|-------|---------|----------|
| **Silence** | No facilitator input for 6+ minutes | High |
| **Frustration** | AI detects rising user frustration | High |
| **Imbalance** | An avatar is contributing <30% of the average | Medium |
| **Phase risk** | Current phase has exceeded 120% of its expected duration | Medium |
| **Pending overflow** | More than 5 contributions waiting for review | Low |

In HUD mode, alerts flash briefly in the notification zone. In Switchable mode, the Alerts screen shows all active alerts with full messages.

---

## Health Score

A score from **1–10** shown in the status bar:

- **8–10** — Session is healthy, good engagement
- **5–7** — Some concerns, check alerts
- **1–4** — Needs attention — frustration or silence detected

The score factors in user frustration level and how long since the last facilitator input.

---

## Setting Up

### 1. Pair the device

In the [Devices](help:session-devices) panel:
1. Click **Pair Device**
2. Find the AR glasses device in the list (capabilities: \`display\`, \`ring-input\`)
3. Select **AR Glasses (facilitator HUD)** from the role dropdown
4. Choose a lease duration (recommended: 8h for a full-day workshop)
5. Click **Pair**

### 2. Start the bridge

On the machine connected to the glasses (via Bluetooth):

\`\`\`bash
# Physical glasses (Bluetooth)
python ar_glasses_bridge.py --url https://your-n8n-url/webhook/device-api

# Specify device and location
python ar_glasses_bridge.py \\
  --url https://your-n8n-url/webhook/device-api \\
  --device-id ar-glasses-001 \\
  --location workshop-room \\
  --glasses-name "Even G2"
\`\`\`

The bridge connects to the G2 via Bluetooth, polls the backend every 3 seconds for display updates, and forwards ring gestures.

### 3. Put on the glasses

The display will show session data as soon as the device is paired and a session is active. Ring gestures work immediately.

---

## Testing Without Hardware

You don't need physical glasses to test the feature:

### Console mode
\`\`\`bash
python ar_glasses_bridge.py --no-ble --url https://your-n8n-url/webhook/device-api
\`\`\`
Prints the display as text in your terminal. Use keyboard keys to simulate ring gestures:
- **J/K** or **Up/Down** — Scroll
- **Enter/Space** — Single tap
- **D** — Double tap
- **M** — Long press (mode toggle)

### Emulator mode
\`\`\`bash
python ar_glasses_bridge.py --emulator --url https://your-n8n-url/webhook/device-api
\`\`\`
Opens a window showing the glasses display as green monochrome text on black (simulating the G2's Micro LED). Same keyboard controls as console mode.

### Standalone emulator (no backend)
\`\`\`bash
python glasses_emulator.py
\`\`\`
Runs with mock session data — useful for testing the display layout and gestures without any backend running.

### Debug frame saving
\`\`\`bash
python ar_glasses_bridge.py --no-ble --save-frames ./frames --url ...
\`\`\`
Saves each rendered frame as a BMP file for offline inspection.

---

## How It Works

The data flows through three layers:

1. **Backend** generates a glasses summary every poll cycle — current phase, pending contributions (sorted by urgency), alerts, health score, and recent task completions
2. **n8n** wraps the summary as a \`display-update\` command and delivers it when the bridge polls
3. **Bridge** renders the data to a 640×350 1-bit BMP image and sends it to the G2 via Bluetooth

Ring gestures flow in reverse:
1. Ring sends touchbar event → G2 → Bridge (via Bluetooth)
2. Bridge classifies the gesture against current display state
3. **Local actions** (scroll, screen switch, mode toggle) update the display immediately
4. **Remote actions** (accept, ignore, request contributions, advance phase) are sent to the backend, which processes them and updates the session

This split between local and remote actions keeps the display responsive — scrolling and mode switches are instant, while session-mutating actions go through the server.

---

## Tips

- **Start in HUD mode** for most of the session — it gives you a constant overview without blocking your view
- **Switch to Switchable mode** when you need detail — e.g. reading full alert messages or reviewing a long contributions queue
- **Renew leases proactively** — The devices panel shows a warning when the lease is expiring. Renew before it expires to avoid losing the connection mid-session
- **Use the emulator first** — Test the display and gestures with the emulator before connecting physical hardware, to familiarise yourself with the ring controls
- **Watch the health score** — A dropping score is your cue to check alerts and re-engage the conversation`;
